import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/lib/apiClient';
import { openOAuthPopup } from '@/features/Auth/OAuthPopup';
import { toast } from 'sonner';

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) throw redirect({ to: '/dashboard' });
  },
  component: LoginPage,
});

function LoginPage() {
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const { data } = await apiClient.post('/api/v1/auth/login', { email, password });
      setAuth(data.access_token);
      navigate({ to: '/dashboard' });
    } catch {
      // The API returns a generic 401 for both a wrong password and an
      // account that only has OAuth (no password), so we cannot distinguish
      // them here — show one clear, actionable message that covers both.
      setError('Incorrect email or password. If you signed up with Google, use "Continue with Google" above.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'github') => {
    try {
      const token = await openOAuthPopup(provider);
      setAuth(token);
      navigate({ to: '/dashboard' });
    } catch (err) {
      if (err instanceof Error && err.message !== 'Login cancelled') {
        toast.error(err.message);
      }
    }
  };

  return (
    <div className="flex min-h-screen font-sans">
      {/* Left panel — brand */}
      <div className="hidden w-[480px] flex-col justify-between bg-brand-dark p-12 lg:flex">
        <div>
          <a href={import.meta.env.VITE_MARKETING_URL || '/'} className="flex items-center gap-3">
            <img
              src="/images/logo-footer.svg"
              alt="KodPrep"
              className="h-10 w-auto"
            />
          </a>
          <div className="mt-10">
            <h2 className="font-heading text-4xl font-semibold leading-tight tracking-tight text-white">
              Build your coding skills, ace your placements.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-neutral-400">
              Practice DSA & SQL problems, take assessments, and track your progress — all in one platform.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4 rounded-xl bg-white/5 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-primary/20">
              <svg className="h-5 w-5 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-white">200+ Problems</p>
              <p className="text-xs text-white/60">DSA & SQL with real execution</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-xl bg-white/5 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-primary/20">
              <svg className="h-5 w-5 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-white">Track Progress</p>
              <p className="text-xs text-white/60">Analytics & placement insights</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex flex-1 items-center justify-center bg-surface-warm px-6">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="mb-8 text-center lg:hidden">
            <img src="/images/logo.svg" alt="KodPrep" className="mx-auto h-12 w-auto" />
          </div>

          <div className="hidden lg:block">
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-text-primary">
              Welcome back
            </h1>
            <p className="mt-2 text-sm text-text-secondary">
              Sign in to continue to your dashboard
            </p>
          </div>

          {/* Card */}
          <div className="mt-8 rounded-xl border border-neutral-200 bg-white p-8 shadow-sm">
            {/* OAuth buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleOAuth('google')}
                className="flex items-center justify-center gap-3 rounded-lg border border-[#747775] px-4 py-3.5 text-sm font-medium text-text-primary transition-colors duration-150 hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </div>

            {/* Divider */}
            <div className="my-6 flex items-center gap-4">
              <div className="h-px flex-1 bg-neutral-200" />
              <span className="text-xs font-medium text-neutral-500">OR</span>
              <div className="h-px flex-1 bg-neutral-200" />
            </div>

            {/* Email/password form */}
            <form onSubmit={handleEmailLogin} className="flex flex-col gap-4">
              {error && (
                <p
                  id="login-error"
                  role="alert"
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5 text-sm text-error"
                >
                  {error}
                </p>
              )}
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-text-primary">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  placeholder="you@university.edu"
                  className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3.5 text-sm text-text-primary placeholder:text-neutral-400 transition-colors duration-150 focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                />
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5">
                <label htmlFor="password" className="col-start-1 row-start-1 text-sm font-medium text-text-primary">
                  Password
                </label>
                <div className="relative col-span-2 row-start-2">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    aria-invalid={!!error}
                    aria-describedby={error ? 'login-error' : undefined}
                    placeholder="Enter your password"
                    className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3.5 pr-12 text-sm text-text-primary placeholder:text-neutral-400 transition-colors duration-150 focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="col-span-2 row-start-3 mt-2 rounded-lg bg-brand-primary px-4 py-3.5 text-[0.9375rem] font-medium text-white transition-all duration-150 ease-out hover:bg-brand-primary-hover hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </button>
                <a href="/auth/forgot-password" className="col-start-2 row-start-1 -my-1 self-center justify-self-end rounded px-1 py-2 text-xs font-medium text-brand-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary">
                  Forgot password?
                </a>
              </div>
            </form>

            <p className="mt-6 text-center text-sm text-text-secondary">
              No account? Contact your organization admin for an invite.
            </p>
          </div>

          <p className="mt-6 text-center text-xs text-neutral-500">
            &copy; 2026 KodPrep. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
